// Utilities for processing tournament bracket data
import * as XLSX from 'xlsx';
import React from 'react';

// Helper function to format time values, handling Date objects
const formatTimeValue = (value) => {
  if (value instanceof Date) {
    // Format as HH:MM
    return value.getHours().toString().padStart(2, '0') + ':' + 
           value.getMinutes().toString().padStart(2, '0');
  }
  return value;
};

// Helper function to safely get cell value
const getCellValue = (jsonData, rowIdx, colIdx) => {
  if (jsonData && jsonData[rowIdx] && colIdx >= 0 && colIdx < jsonData[rowIdx].length) {
    return jsonData[rowIdx][colIdx];
  }
  return null;
};

// Helper function to process bracket data for divisions
export const processBracketData = (worksheet, divisionType, bracketType) => {
  // Convert worksheet to JSON with header: 1 to get arrays instead of objects
  const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
  
  // Array to store all matches
  const bracketMatches = [];
  
  if (bracketType === 'light') {
    // Define court mapping for bracket
    const lightBracketCourts = {
      // Column ranges for courts
      "1 Aikštelė": { minCol: 2, maxCol: 5, rowRanges: [[41, 44]] },  // Final
      "2 Aikštelė": { minCol: 9, maxCol: 13, rowRanges: [[41, 44]] }, // Bronze
      "4 Aikštelė": { minCol: 2, maxCol: 5, rowRanges: [[47, 54]] },  // C-F rows 48-54
      "5 Aikštelė": { minCol: 9, maxCol: 12, rowRanges: [[47, 54]] }, // J-M rows 48-54
      "6 Aikštelė": { minCol: 16, maxCol: 19, rowRanges: [[47, 52]] } // Q-T rows 48-52
    };
    
    // Function to determine court based on cell position for light bracket
    const determineCourtForLightBracket = (row, col) => {
      for (const [courtName, range] of Object.entries(lightBracketCourts)) {
        const { minCol, maxCol, rowRanges } = range;
        
        if (col >= minCol && col <= maxCol) {
          for (const [minRow, maxRow] of rowRanges) {
            if (row >= minRow && row <= maxRow) {
              return courtName;
            }
          }
        }
      }
      return "Court not specified";
    };
    
    // Custom text mappings for each game
    const customGameTexts = {
      // Add mappings using game number as key and an object with teamA, teamB texts as values
      "Game 1": { teamA: "8 Vieta", teamB: "9 Vieta" },
      "Game 2": { teamA: "5 Vieta", teamB: "12 Vieta" },
      "Game 3": { teamA: "6 Vieta", teamB: "11 Vieta" },
      "Game 4": { teamA: "7 Vieta", teamB: "10 Vieta" },
      "Game 5": { teamA: "Game 1 Winner", teamB: "1 Vieta" },
      "Game 6": { teamA: "Game 2 Winner", teamB: "4 Vieta" },
      "Game 7": { teamA: "Game 3 Winner", teamB: "3 Vieta" },
      "Game 8": { teamA: "Game 4 Winner", teamB: "2 Vieta" },
      "Game 9": { teamA: "Game 5 Looser", teamB: "Game 4 Looser" },
      "Game 10": { teamA: "Game 3 Looser", teamB: "Game 6 Looser" },
      "Game 11": { teamA: "Game 7 Looser", teamB: "Game 2 Looser" },
      "Game 12": { teamA: "Game 1 Looser", teamB: "Game 8 Looser" },
      "Game 13": { teamA: "Game 6 Winner", teamB: "Game 5 Winner" },
      "Game 14": { teamA: "Game 8 Winner", teamB: "Game 7 Winner" },
      "Game 15": { teamA: "Game 12 Winner", teamB: "Game 11 Winner" },
      "Game 16": { teamA: "Game 9 Winner", teamB: "Game 10 Winner" },
      "Game 17": { teamA: "Game 15 Winner", teamB: "Game 14 Looser" },
      "Game 18": { teamA: "Game 16 Winner", teamB: "Game 13 Looser" },
      "Game 19": { teamA: "Game 13 Winner", teamB: "Game 17 Winner" },
      "Game 20": { teamA: "Game 18 Winner", teamB: "Game 16 Winner" },
      "Final": { teamA: "Game 19 Winner", teamB: "Game 20 Winner" },
      "Bronze": { teamA: "Game 20 Looser", teamB: "Game 19 Loser" },
    };
    
    // Process specific cells for Light Division
    const lightDivisionMatches = [
      // Game 1: C48 vs F48 scores D48, E48, referee G48
      { row: 47, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 1" },
      // Game 2: J48 vs M48 scores K48, L48, referee N48
      { row: 47, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 2" },
      // Game 3: Q48 vs T48 scores R48, S48, referee U48
      { row: 47, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 3" },
      // Game 4: Q49 vs T49 scores R49, S49, referee U49
      { row: 48, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 4" },
      // Game 5: C49 vs F49 scores D49, E49, referee G49
      { row: 48, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 5" },
      // Game 6: J49 vs M49 scores K49, L49, referee N49
      { row: 48, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 6" },
      // Game 7: J50 vs M50 scores K50, L50, referee N50
      { row: 49, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 7" },
      // Game 8:  Q50 vs T50 scores R50, S50, referee U50
      { row: 49, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 8" },
      // Game 9: C50 vs F50 scores D50, E50, referee G50
      { row: 49, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 9" },
      // Game 10: Q51 vs T51 scores R51, S51, referee U51
      { row: 50, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 10" },
      // Game 11: J51 vs M51 scores K51, L51, referee N51
      { row: 50, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 11" },
      // Game 12: C51 vs F51 scores D51,E51, referee G51
      { row: 50, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 12" },
      // Game 13: J52 vs M52 scores K52, L52, referee N52
      { row: 51, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 13" },
      // Game 14: Q52 vs T52 scores R52, S52, referee U52
      { row: 51, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 14" },
      // Game 15: C52 vs F52 scores D52, E52, referee G52
      { row: 51, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 15" },
      // Game 16: J53 vs M53 scores K53, L53, referee N53
      { row: 52, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 16" },
      // Game 17: C53 vs F53 scores D53, E53, referee G53
      { row: 52, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 17" },
      // Game 18: J54 vs M54 scores K54, L54, referee N54
      { row: 53, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 18" },
      // Game 19: C54 vs F54 scores D54, E54, referee G54
      { row: 53, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 19" },
      // Game 20 (moved to 2nd court): J42 vs M42 scores K42, L42, referee N42
      { row: 41, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 20" },
      // Bronze: J43 vs M43 scores K43, L43, referee N43
      { row: 42, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Bronze" },
      // Final: C43 vs F43 scores D43, E43, referee G43
      { row: 42, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Final" }
    ];
    
    // Process each match definition
    lightDivisionMatches.forEach((matchDef, index) => {
      const { row, teamACol, teamAScoreCol, teamBScoreCol, teamBCol, refereeCol, gameNumber } = matchDef;
      
      // Get time from column A
      const time = formatTimeValue(getCellValue(jsonData, row, 0) || "");
      
      // Get all other values
      let teamA = getCellValue(jsonData, row, teamACol);
      const teamAScore = getCellValue(jsonData, row, teamAScoreCol);
      const teamBScore = getCellValue(jsonData, row, teamBScoreCol);
      let teamB = getCellValue(jsonData, row, teamBCol);
      // Get referee value
      const referee = getCellValue(jsonData, row, refereeCol) || "TBD";
      
      // Apply custom text if team is "TBD" or missing
      if (customGameTexts[gameNumber]) {
        // Apply custom teamA text if available and team is not defined in Excel
        if (customGameTexts[gameNumber].teamA && (!teamA || teamA === "TBD")) {
          teamA = customGameTexts[gameNumber].teamA;
        }
        
        // Apply custom teamB text if available and team is not defined in Excel
        if (customGameTexts[gameNumber].teamB && (!teamB || teamB === "TBD")) {
          teamB = customGameTexts[gameNumber].teamB;
        }
      }
      
      // Use default "TBD" if no custom text and no Excel value
      teamA = teamA || "TBD";
      teamB = teamB || "TBD";
      
      // Determine court dynamically
      const court = determineCourtForLightBracket(row, teamACol);
      
      // Create the match object
      const match = {
        id: (index + 1).toString(),
        gameNumber,
        time,
        teamA,
        teamAScore: teamAScore !== undefined ? teamAScore : "",
        teamB,
        teamBScore: teamBScore !== undefined ? teamBScore : "",
        court,
        referee // Add referee information
      };
      
      bracketMatches.push(match);
    });
  } 
  else if (bracketType === 'hard') {
    // Hard Division (Didžioji Taurė)
    // Use the same custom game texts as light division
    const customGameTexts = {
      "Game 1": { teamA: "8 Vieta", teamB: "9 Vieta" },
      "Game 2": { teamA: "5 Vieta", teamB: "12 Vieta" },
      "Game 3": { teamA: "6 Vieta", teamB: "11 Vieta" },
      "Game 4": { teamA: "7 Vieta", teamB: "10 Vieta" },
      "Game 5": { teamA: "Game 1 Winner", teamB: "1 Vieta" },
      "Game 6": { teamA: "Game 2 Winner", teamB: "4 Vieta" },
      "Game 7": { teamA: "Game 3 Winner", teamB: "3 Vieta" },
      "Game 8": { teamA: "Game 4 Winner", teamB: "2 Vieta" },
      "Game 9": { teamA: "Game 5 Looser", teamB: "Game 4 Looser" },
      "Game 10": { teamA: "Game 3 Looser", teamB: "Game 6 Looser" },
      "Game 11": { teamA: "Game 7 Looser", teamB: "Game 2 Looser" },
      "Game 12": { teamA: "Game 1 Looser", teamB: "Game 8 Looser" },
      "Game 13": { teamA: "Game 6 Winner", teamB: "Game 5 Winner" },
      "Game 14": { teamA: "Game 8 Winner", teamB: "Game 7 Winner" },
      "Game 15": { teamA: "Game 12 Winner", teamB: "Game 11 Winner" },
      "Game 16": { teamA: "Game 9 Winner", teamB: "Game 10 Winner" },
      "Game 17": { teamA: "Game 15 Winner", teamB: "Game 14 Looser" },
      "Game 18": { teamA: "Game 16 Winner", teamB: "Game 13 Looser" },
      "Game 19": { teamA: "Game 13 Winner", teamB: "Game 17 Winner" },
      "Game 20": { teamA: "Game 18 Winner", teamB: "Game 16 Winner" },
      "Final": { teamA: "Game 19 Winner", teamB: "Game 20 Winner" },
      "Bronze": { teamA: "Game 20 Looser", teamB: "Game 19 Loser" },
    };
    
    // Define court mapping for bracket
    const hardBracketCourts = {
      // Column ranges for courts - shifted to courts 1, 2, 3 for hard division
      "1 Aikštelė": { minCol: 2, maxCol: 7, rowRanges: [[34, 45]] },  // Final
      "2 Aikštelė": { minCol: 9, maxCol: 12, rowRanges: [[34, 45]] }, // Bronze
      "3 Aikštelė": { minCol: 16, maxCol: 19, rowRanges: [[34, 45]] } // Replace court 6
    };
    
    // Function to determine court based on cell position for hard bracket
    const determineCourForHardBracket = (row, col) => {
      for (const [courtName, range] of Object.entries(hardBracketCourts)) {
        const { minCol, maxCol, rowRanges } = range;
        
        if (col >= minCol && col <= maxCol) {
          for (const [minRow, maxRow] of rowRanges) {
            if (row >= minRow && row <= maxRow) {
              return courtName;
            }
          }
        }
      }
      return "Court not specified";
    };
    
    // Process specific cells for Hard Division - same structure as light but using courts 1, 2, 3
    const hardDivisionMatches = [
      // Game 1: C35 vs F35 scores D35, E35, referee G35
      { row: 34, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 1" },
      // Game 2: J35 vs M35 scores K35, L35, referee N35
      { row: 34, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 2" },
      // Game 3: Q35 vs T35 scores R35, S35, referee U35
      { row: 34, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 3" },
      // Game 4: Q36 vs T36 scores R36, S36, referee U36
      { row: 35, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 4" },
      // Game 5: C36 vs F36 scores D36, E36, referee G36
      { row: 35, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 5" },
      // Game 6: J36 vs M36 scores K36, L36, referee N36
      { row: 35, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 6" },
      // Game 7: J37 vs M37 scores K37, L37, referee N37
      { row: 36, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 7" },
      // Game 8: Q37 vs T37 scores R37, S37, referee U37
      { row: 36, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 8" },
      // Game 9: C37 vs F37 scores D37, E37, referee G37
      { row: 36, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 9" },
      // Game 10: Q38 vs T38 scores R38, S38, referee U38
      { row: 37, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 10" },
      // Game 11: J38 vs M38 scores K38, L38, referee N38
      { row: 37, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 11" },
      // Game 12: C38 vs F38 scores D38, E38, referee G38
      { row: 37, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 12" },
      // Game 13: J39 vs M39 scores K39, L39, referee N39
      { row: 38, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 13" },
      // Game 14: Q39 vs T39 scores R39, S39, referee U39
      { row: 38, teamACol: 16, teamAScoreCol: 17, teamBScoreCol: 18, teamBCol: 19, refereeCol: 20, gameNumber: "Game 14" },
      // Game 15: C39 vs F39 scores D39, E39, referee G39
      { row: 38, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 15" },
      // Game 16: J40 vs M40 scores K40, L40, referee N40
      { row: 39, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 16" },
      // Game 17: C40 vs F40 scores D40, E40, referee G40
      { row: 39, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 17" },
      // Game 18: J41 vs M41 scores K41, L41, referee N41
      { row: 40, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Game 18" },
      // Game 19: C41 vs F41 scores D41, E41, referee G41
      { row: 40, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 19" },
      // Game 20: C42 vs F42 scores D42, E42, referee G42
      { row: 41, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Game 20" },
      // Bronze: J44 vs M44 scores K44, L44, referee N44
      { row: 43, teamACol: 9, teamAScoreCol: 10, teamBScoreCol: 11, teamBCol: 12, refereeCol: 13, gameNumber: "Bronze" },
      // Final: C44 vs F44 scores D44, E44, referee G44
      { row: 43, teamACol: 2, teamAScoreCol: 3, teamBScoreCol: 4, teamBCol: 5, refereeCol: 6, gameNumber: "Final" }
    ];
    
    // Process each match definition
    hardDivisionMatches.forEach((matchDef, index) => {
      const { row, teamACol, teamAScoreCol, teamBScoreCol, teamBCol, refereeCol, gameNumber } = matchDef;
      
      // Get time from column A
      const time = formatTimeValue(getCellValue(jsonData, row, 0) || "");
      
      // Get all other values
      let teamA = getCellValue(jsonData, row, teamACol);
      const teamAScore = getCellValue(jsonData, row, teamAScoreCol);
      const teamBScore = getCellValue(jsonData, row, teamBScoreCol);
      let teamB = getCellValue(jsonData, row, teamBCol);
      // Get referee value
      const referee = getCellValue(jsonData, row, refereeCol) || "TBD";
      
      // Apply custom text if team is "TBD" or missing
      if (customGameTexts[gameNumber]) {
        // Apply custom teamA text if available and team is not defined in Excel
        if (customGameTexts[gameNumber].teamA && (!teamA || teamA === "TBD")) {
          teamA = customGameTexts[gameNumber].teamA;
        }
        
        // Apply custom teamB text if available and team is not defined in Excel
        if (customGameTexts[gameNumber].teamB && (!teamB || teamB === "TBD")) {
          teamB = customGameTexts[gameNumber].teamB;
        }
      }
      
      // Use default "TBD" if no custom text and no Excel value
      teamA = teamA || "TBD";
      teamB = teamB || "TBD";
      
      // Determine court dynamically 
      const court = determineCourForHardBracket(row, teamACol);
      
      // Create the match object
      const match = {
        id: (index + 1).toString(),
        gameNumber,
        time,
        teamA,
        teamAScore: teamAScore !== undefined ? teamAScore : "",
        teamB,
        teamBScore: teamBScore !== undefined ? teamBScore : "",
        court,
        referee // Add referee information
      };
      
      bracketMatches.push(match);
    });
  }

  return { matches: bracketMatches, rounds: [] };
};

// Updated renderSimpleBracket function to include referee column
// Updated renderSimpleBracket function to support search highlighting
export const renderSimpleBracket = (bracketData, highlightText) => {
  const { matches } = bracketData;
  
  if (!matches || matches.length === 0) {
    return (
      <div className="no-data-message">
        <p>No bracket data available yet.</p>
      </div>
    );
  }

  // Helper function to render text with potential highlighting
  const renderText = (text) => {
    // If highlightText function is provided (from search component), use it
    // Otherwise just return the text as is
    return highlightText ? highlightText(text) : text;
  };

  return (
    <div className="bracket-matches">
      <table>
        <thead>
          <tr>
            <th>GAME</th>
            <th>TIME</th>
            <th>TEAM A</th>
            <th>SCORE</th>
            <th>SCORE</th>
            <th>TEAM B</th>
            <th>COURT</th>
            <th>REFEREE</th>
          </tr>
        </thead>
        <tbody>
          {matches.map((match, matchIndex) => {
            // Parse scores to numbers for comparison (if they exist)
            const scoreA = match.teamAScore !== null && match.teamAScore !== undefined && match.teamAScore !== "" 
              ? Number(match.teamAScore) 
              : null;
            const scoreB = match.teamBScore !== null && match.teamBScore !== undefined && match.teamBScore !== "" 
              ? Number(match.teamBScore) 
              : null;
            
            // Determine winner styling
            const teamAWinner = scoreA !== null && scoreB !== null && scoreA > scoreB;
            const teamBWinner = scoreA !== null && scoreB !== null && scoreB > scoreA;
            
            // Check if this is a special game (Final or Bronze)
            const isFinal = match.gameNumber === "Final";
            const isBronze = match.gameNumber === "Bronze";
            const rowClass = isFinal ? "final-game" : (isBronze ? "bronze-game" : "");
            
            // Check if this row is part of search results
            const teamAHighlighted = match.teamAMatch;
            const teamBHighlighted = match.teamBMatch;
            
            return (
              <tr key={matchIndex} className={rowClass}>
                <td>{match.gameNumber}</td>
                <td>{match.time}</td>
                <td className={teamAWinner ? "winner" : ""} 
                    style={teamAHighlighted ? {backgroundColor: "rgba(33, 150, 243, 0.1)"} : {}}>
                  {renderText(match.teamA)}
                </td>
                <td className="score">{match.teamAScore}</td>
                <td className="score">{match.teamBScore}</td>
                <td className={teamBWinner ? "winner" : ""}
                    style={teamBHighlighted ? {backgroundColor: "rgba(33, 150, 243, 0.1)"} : {}}>
                  {renderText(match.teamB)}
                </td>
                <td>{match.court}</td>
                <td style={match.refereeMatch ? {backgroundColor: "rgba(33, 150, 243, 0.1)"} : {}}>
                  {renderText(match.referee)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};